// D-1046: 全ルートの画面利用(閲覧回数・滞在秒)と巡回の段取り時間を、ルートの層で 1 か所で測る。
//   App.jsx の <RouteUsageTracker /> から 1 回だけ使う。各画面のコードには手を入れない。
//
//   ★測るもの: 画面に入った回数(views)と、その画面が見えていた秒数(seconds)。
//     key は URL のパターンから決まる固定の文字列(src/constants/routePageKeys.js)。
//     URL の値(:boothCode 等)・クエリ・入力値・画面の中身は一切送らない。
//   ★送り方: services/usageBuffer.js に足し込むだけ。通信はまとめ送り(30 秒後 / 画面が隠れたとき)。
//   ★self: true の画面(D-068 の分析ページ)は画面の中で既に数えているので、ここでは数えない。
//   ★未ログイン(staffId なし)は何もしない。
//   ★巡回の段取り時間(flow:)は「ブース入力画面に入ってから次のブース / 一覧へ移るまで」を
//     ルートの移り変わりだけで測る。巡回画面(PatrolBoothInputPage / usePatrolForm)には触らない。
//     次のブースかどうかは遷移の state.boothIndex(数値)が前より 1 大きいかで見る(既存の goNextBooth が渡している)。
import { useEffect, useRef } from 'react'
import { useLocation, matchRoutes } from 'react-router-dom'
import { useAuth } from './useAuth'
import { ROUTE_PAGE_KEYS, FLOW_KEY } from '../constants/routePageKeys'
import { USAGE_DWELL_CLIP_SECONDS } from '../constants/pageKeys'
import { recordUsage, flushUsage, installUsageFlushListeners } from '../services/usageBuffer'

const ROUTE_OBJECTS = ROUTE_PAGE_KEYS.map(entry => ({ path: entry.path, entry }))

const PATROL_BOOTH_PATH = '/clawsupport/booth/:boothCode'
const PATROL_STORE_PATH = '/clawsupport/store/:storeCode'

/** pathname → ROUTE_PAGE_KEYS の行(該当なしは null)。react-router と同じ順位付けで選ぶ。 */
export function resolveRouteEntry(pathname) {
  try {
    const matches = matchRoutes(ROUTE_OBJECTS, pathname || '/')
    if (!matches || matches.length === 0) return null
    return matches[matches.length - 1].route.entry ?? null
  } catch {
    return null
  }
}

/** pathname → ルート側で数える page_key(数えないなら null)。 */
export function resolveTrackedPageKey(pathname) {
  const e = resolveRouteEntry(pathname)
  if (!e || !e.key || e.self) return null
  return e.key
}

const clip = (sec) => Math.min(USAGE_DWELL_CLIP_SECONDS, Math.max(0, Math.floor(Number(sec) || 0)))

function readBoothIndex(state) {
  const v = state && typeof state === 'object' ? state.boothIndex : null
  return Number.isInteger(v) ? v : null
}

export function useRouteUsageTracking() {
  const { staffId } = useAuth()
  const location = useLocation()
  // cur: 今見ている画面 1 件分の状態
  const curRef = useRef(null)

  // 見えていた秒数を cur.total に足し込み、計測を止める(再開は resume)。
  const pause = (cur, now) => {
    if (cur.visibleSince != null) {
      cur.total += (now - cur.visibleSince) / 1000
      cur.visibleSince = null
    }
  }
  // まだ送っていない滞在秒を足し込む(1 画面の合計は 600 秒で頭打ち)。
  const recordPendingSeconds = (cur) => {
    if (!cur.pageKey) return
    const capped = clip(cur.total)
    const add = capped - cur.sent
    if (add > 0) {
      recordUsage(cur.staffId, cur.pageKey, { seconds: add })
      cur.sent = capped
    }
  }

  useEffect(() => { installUsageFlushListeners() }, [])

  // 画面(pathname)かログイン者が変わったとき: 前の画面を締めて、次の画面を始める。
  useEffect(() => {
    try {
      const now = Date.now()
      const prev = curRef.current
      const entry = resolveRouteEntry(location.pathname)
      const boothIndex = readBoothIndex(location.state)

      if (prev) {
        pause(prev, now)
        recordPendingSeconds(prev)
        // 巡回の段取り時間: ブース入力画面から次へ移ったとき
        if (prev.path === PATROL_BOOTH_PATH && prev.staffId && prev.staffId === staffId) {
          const sec = clip(prev.total)
          if (entry?.path === PATROL_BOOTH_PATH && prev.boothIndex != null && boothIndex === prev.boothIndex + 1) {
            recordUsage(prev.staffId, FLOW_KEY.PATROL_BOOTH_NEXT, { views: 1, seconds: sec })
          } else if (entry?.path === PATROL_STORE_PATH) {
            recordUsage(prev.staffId, FLOW_KEY.PATROL_BOOTH_TO_LIST, { views: 1, seconds: sec })
          }
        }
        // ログアウト等で人が変わったら、前の人の分はここで送っておく
        if (prev.staffId !== staffId) flushUsage()
      }

      if (!staffId || !entry) { curRef.current = null; return }
      const pageKey = entry.key && !entry.self ? entry.key : null
      if (pageKey) recordUsage(staffId, pageKey, { views: 1 })
      const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden'
      curRef.current = {
        staffId,
        path: entry.path,
        pageKey,
        boothIndex,
        total: 0,
        sent: 0,
        visibleSince: visible ? now : null,
      }
    } catch {
      /* 計測は画面を止めない */
    }
    // location.state は pathname と同時に変わる。state だけの変化(同じ画面)は新しい閲覧にしない。
  }, [location.pathname, staffId]) // eslint-disable-line react-hooks/exhaustive-deps

  // 画面が隠れた / 閉じられる: 滞在秒を足してからすぐ送る。戻ったら計測を再開。
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return
    const onHide = () => {
      try {
        const cur = curRef.current
        if (cur) { pause(cur, Date.now()); recordPendingSeconds(cur) }
        flushUsage()
      } catch { /* noop */ }
    }
    const onVis = () => {
      if (document.visibilityState === 'hidden') { onHide(); return }
      const cur = curRef.current
      if (cur && cur.visibleSince == null) cur.visibleSince = Date.now()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', onHide)
      onHide()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}
